import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import {
  Paper,
  Button,
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import apiService from '../../services/api';

interface ProspectsSearchProps {
  onNotification: (message: string, severity: 'success' | 'error') => void;
}

interface FilterOptions {
  job_titles: string[];
  departments: string[];
  industries: string[];
  cities: string[];
  countries: string[];
  accounts: string[];
  email_validation_statuses: string[];
}

interface Prospect {
  _id: string;
  email: string;
  first_name: string;
  last_name: string;
  job_title: string;
  department: string;
  company: string;
  industry: string;
  city: string;
  country: string;
  account: string;
  email_validation: {
    verification_result: string;
  };
  created_at: string;
}

interface SearchResults {
  prospects: Prospect[];
  total: number;
  skip: number;
  limit: number;
}

const ProspectsSearch: React.FC<ProspectsSearchProps> = ({ onNotification }) => {
  // Filter options
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    job_titles: [],
    departments: [],
    industries: [],
    cities: [],
    countries: [],
    accounts: [],
    email_validation_statuses: []
  });

  // Selected filters
  const [selectedJobTitles, setSelectedJobTitles] = useState<string[]>([]);
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [selectedEmailStatuses, setSelectedEmailStatuses] = useState<string[]>([]);

  // Search results and state
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);

  // Template generation state
  const [templateTypes, setTemplateTypes] = useState<string[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [emailQuality, setEmailQuality] = useState<string>('all');
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState<boolean>(false);

  // Load filter options on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const response = await apiService.getProspectsFilters();
        if (response?.filters) {
          setFilterOptions(response.filters);
        }
      } catch (error) {
        logger.error('Failed to fetch filter options:', error);
        onNotification('Failed to load filter options', 'error');
      }
    };

    const fetchTemplateTypes = async () => {
      try {
        const response = await apiService.getTemplateTypes();
        if (response) {
          setTemplateTypes(Object.keys(response));
        }
      } catch (error) {
        logger.error('Failed to fetch template types:', error);
      }
    };

    fetchFilters();
    fetchTemplateTypes();
  }, []);

  // Check if at least one filter is selected
  const hasFiltersSelected = (): boolean => {
    return [
      selectedJobTitles,
      selectedDepartments,
      selectedIndustries,
      selectedCities,
      selectedCountries,
      selectedAccounts,
      selectedEmailStatuses
    ].some((selection) => selection.length > 0);
  };

  const toggleSelection = (
    value: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setter((prev) =>
      prev.includes(value) ? prev.filter((item) => item !== value) : [...prev, value]
    );
  };

  const filterConfig: Array<{
    label: string;
    options: string[];
    selected: string[];
    setter: React.Dispatch<React.SetStateAction<string[]>>;
  }> = [
    { label: 'Job Title', options: filterOptions.job_titles, selected: selectedJobTitles, setter: setSelectedJobTitles },
    { label: 'Department', options: filterOptions.departments, selected: selectedDepartments, setter: setSelectedDepartments },
    { label: 'Industry', options: filterOptions.industries, selected: selectedIndustries, setter: setSelectedIndustries },
    { label: 'City', options: filterOptions.cities, selected: selectedCities, setter: setSelectedCities },
    { label: 'Country', options: filterOptions.countries, selected: selectedCountries, setter: setSelectedCountries },
    { label: 'Account/Client', options: filterOptions.accounts, selected: selectedAccounts, setter: setSelectedAccounts },
    { label: 'Email Status', options: filterOptions.email_validation_statuses, selected: selectedEmailStatuses, setter: setSelectedEmailStatuses }
  ];

  const buildOptionId = (label: string, option: string, index: number) =>
    `${label}-${index}-${option}`.replace(/[^a-zA-Z0-9_-]/g, '-');

  // Build filters object for API call
  const buildFilters = () => {
    const filters: any = {};
    if (selectedJobTitles.length) filters.job_title = selectedJobTitles;
    if (selectedDepartments.length) filters.department = selectedDepartments;
    if (selectedIndustries.length) filters.industry = selectedIndustries;
    if (selectedCities.length) filters.city = selectedCities;
    if (selectedCountries.length) filters.country = selectedCountries;
    if (selectedAccounts.length) filters.account = selectedAccounts;
    if (selectedEmailStatuses.length) filters.email_status = selectedEmailStatuses;
    return filters;
  };

  // Handle search
  const handleSearch = async () => {
    if (!hasFiltersSelected()) {
      onNotification('Please select at least one filter', 'error');
      return;
    }

    setIsSearching(true);
    setPage(0);

    try {
      const filters = buildFilters();
      filters.page = 1;
      filters.page_size = rowsPerPage;

      const response = await apiService.searchProspects(filters);
      setSearchResults(response);
      
      onNotification(`Found ${response.total} prospects matching your criteria`, 'success');
    } catch (error: any) {
      logger.error('Failed to search prospects:', error);
      onNotification(error.message || 'Failed to search prospects', 'error');
    } finally {
      setIsSearching(false);
    }
  };

  // Handle pagination
  const handleChangePage = async (event: unknown, newPage: number) => {
    setIsSearching(true);

    try {
      const filters = buildFilters();
      filters.page = newPage + 1;
      filters.page_size = rowsPerPage;

      const response = await apiService.searchProspects(filters);
      setSearchResults(response);
      setPage(newPage);
    } catch (error: any) {
      logger.error('Failed to load page:', error);
      onNotification(error.message || 'Failed to load page', 'error');
    } finally {
      setIsSearching(false);
    }
  };

  const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);

    if (searchResults) {
      setIsSearching(true);
      try {
        const filters = buildFilters();
        filters.page = 1;
        filters.page_size = newRowsPerPage;

        const response = await apiService.searchProspects(filters);
        setSearchResults(response);
      } catch (error: any) {
        logger.error('Failed to update page size:', error);
        onNotification(error.message || 'Failed to update page size', 'error');
      } finally {
        setIsSearching(false);
      }
    }
  };

  // Download CSV
  const handleDownloadCSV = async () => {
    if (!searchResults || searchResults.prospects.length === 0) {
      onNotification('No search results to download', 'error');
      return;
    }

    setIsDownloading(true);

    try {
      const filters = buildFilters();
      const blob = await apiService.downloadProspects(filters);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `Prospects_Export_${timestamp}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onNotification('CSV downloaded successfully!', 'success');
    } catch (error: any) {
      logger.error('Failed to download CSV:', error);
      onNotification(error.message || 'Failed to download CSV', 'error');
    } finally {
      setIsDownloading(false);
    }
  };

  // Download Audience Template
  const handleDownloadTemplate = async () => {
    if (!selectedTemplate) {
      onNotification('Please select a template type', 'error');
      return;
    }

    if (!searchResults || searchResults.prospects.length === 0) {
      onNotification('No prospects to generate template from', 'error');
      return;
    }

    setIsGeneratingTemplate(true);

    try {
      const filters = buildFilters();
      
      // Include email quality only for MailerCloud
      if (selectedTemplate === 'MailerCloud' && emailQuality !== 'all') {
        filters.email_quality = emailQuality;
      }

      const blob = await apiService.generateAudienceTemplateWithFilters(
        selectedTemplate,
        filters
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.download = `${selectedTemplate}_Template_${timestamp}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onNotification(`${selectedTemplate} template downloaded successfully!`, 'success');
    } catch (error: any) {
      logger.error('Failed to generate template:', error);
      onNotification(error.message || 'Failed to generate template', 'error');
    } finally {
      setIsGeneratingTemplate(false);
    }
  };

  // Reset filters
  const handleResetFilters = () => {
    setSelectedJobTitles([]);
    setSelectedDepartments([]);
    setSelectedIndustries([]);
    setSelectedCities([]);
    setSelectedCountries([]);
    setSelectedAccounts([]);
    setSelectedEmailStatuses([]);
    setSearchResults(null);
    setPage(0);
    setSelectedTemplate('');
    setEmailQuality('all');
  };

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: 'calc(100vh - 200px)',
    }}>
      {/* Filters Section */}
      <Paper sx={{ p: 3, mb: 3, mx: 2 }}>
        <Typography variant="h6" gutterBottom>
          Search Filters (Select at least one)
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {filterConfig.map((config) => (
            <Box
              key={config.label}
              sx={{
                flex: '1 1 240px',
                minWidth: 240,
                maxWidth: 320
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {config.label}
              </Typography>
              <div className="multi-select-dropdown">
                {config.options.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No options available
                  </Typography>
                ) : (
                  config.options.map((option, index) => {
                    const optionId = buildOptionId(config.label, option, index);
                    return (
                      <div key={optionId} className="checkbox-item">
                        <input
                          type="checkbox"
                          id={optionId}
                          checked={config.selected.includes(option)}
                          onChange={() => toggleSelection(option, config.setter)}
                          disabled={isSearching}
                        />
                        <label htmlFor={optionId}>{option}</label>
                      </div>
                    );
                  })
                )}
              </div>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                {config.selected.length > 0
                  ? `Selected: ${config.selected.join(', ')}`
                  : 'No selection'}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={isSearching ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={!hasFiltersSelected() || isSearching}
            sx={{
              backgroundColor: 'var(--e-global-color-secondary)',
              '&:hover': {
                backgroundColor: 'var(--e-global-color-secondary)',
                opacity: 0.8
              }
            }}
          >
            {isSearching ? 'Searching...' : 'Search'}
          </Button>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleResetFilters}
            disabled={isSearching}
            sx={{
              borderColor: 'var(--e-global-color-secondary)',
              color: 'var(--e-global-color-secondary)',
              '&:hover': {
                borderColor: 'var(--e-global-color-secondary)',
                backgroundColor: 'rgba(var(--e-global-color-secondary-rgb), 0.1)'
              }
            }}
          >
            Reset Filters
          </Button>

          {/* Download CSV Button - appears after results */}
          {searchResults && searchResults.prospects.length > 0 && (
            <Button
              variant="contained"
              startIcon={isDownloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadCSV}
              disabled={isDownloading}
              sx={{
                backgroundColor: '#2e7d32',
                '&:hover': {
                  backgroundColor: '#2e7d32',
                  opacity: 0.8
                }
              }}
            >
              {isDownloading ? 'Downloading...' : 'Download CSV'}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Results Section */}
      {searchResults && searchResults.prospects.length > 0 && (
        <>
          {/* Generate Audience Templates Section */}
          <Paper sx={{ p: 3, mb: 3, mx: 2 }}>
            <Typography variant="h6" gutterBottom>
              Generate Audience Templates
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
              {/* Template Type Dropdown */}
              <FormControl fullWidth>
                <InputLabel>Select Template Type</InputLabel>
                <Select
                  value={selectedTemplate}
                  onChange={(e) => {
                    setSelectedTemplate(e.target.value);
                    setEmailQuality('all'); // Reset when changing template
                  }}
                  label="Select Template Type"
                  disabled={isGeneratingTemplate}
                >
                  <MenuItem value="">Choose template...</MenuItem>
                  {templateTypes.map((template) => (
                    <MenuItem key={template} value={template}>
                      {template}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* MailerCloud Email Quality Radio Buttons */}
              {selectedTemplate === 'MailerCloud' && (
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Email Quality:
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {[
                      { value: 'all', label: 'All' },
                      { value: 'deliverable', label: 'Deliverable' },
                      { value: 'accept-all', label: 'Accept-All' },
                      { value: 'unknown', label: 'Unknown' }
                    ].map((option) => (
                      <label key={option.value} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="emailQuality"
                          value={option.value}
                          checked={emailQuality === option.value}
                          onChange={(e) => setEmailQuality(e.target.value)}
                          style={{ marginRight: '8px' }}
                        />
                        <Typography variant="body2">{option.label}</Typography>
                      </label>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Download Template Button */}
              <Button
                variant="contained"
                startIcon={isGeneratingTemplate ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                onClick={handleDownloadTemplate}
                disabled={!selectedTemplate || isGeneratingTemplate}
                sx={{
                  backgroundColor: 'var(--e-global-color-secondary)',
                  '&:hover': {
                    backgroundColor: 'var(--e-global-color-secondary)',
                    opacity: 0.8
                  }
                }}
              >
                {isGeneratingTemplate ? 'Generating...' : 'Download Template'}
              </Button>
            </Box>
          </Paper>

          {/* Results Table */}
          <Paper sx={{ mx: 2 }}>
            <Typography variant="h6" sx={{ p: 2, borderBottom: '1px solid #ddd' }}>
              Search Results ({searchResults.total} prospects)
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>Account</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Email Status</strong></TableCell>
                    <TableCell><strong>First Name</strong></TableCell>
                    <TableCell><strong>Last Name</strong></TableCell>
                    <TableCell><strong>Job Title</strong></TableCell>
                    <TableCell><strong>Company</strong></TableCell>
                    <TableCell><strong>Department</strong></TableCell>
                    <TableCell><strong>Industry</strong></TableCell>
                    <TableCell><strong>City</strong></TableCell>
                    <TableCell><strong>State</strong></TableCell>
                    <TableCell><strong>Country</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.prospects.map((prospect) => (
                    <TableRow key={prospect._id} hover>
                      <TableCell>{prospect.account}</TableCell>
                      <TableCell>{prospect.email}</TableCell>
                      <TableCell>
                        {prospect.email_validation?.verification_result || 'N/A'}
                      </TableCell>
                      <TableCell>{prospect.first_name}</TableCell>
                      <TableCell>{prospect.last_name}</TableCell>
                      <TableCell>{prospect.job_title || 'N/A'}</TableCell>
                      <TableCell>{prospect.company}</TableCell>
                      <TableCell>{prospect.department || 'N/A'}</TableCell>
                      <TableCell>{prospect.industry || 'N/A'}</TableCell>
                      <TableCell>{prospect.city || 'N/A'}</TableCell>
                      <TableCell>{prospect.state || 'N/A'}</TableCell>
                      <TableCell>{prospect.country || 'N/A'}</TableCell>
                      <TableCell>{prospect.phone || 'N/A'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={searchResults.total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
        </>
      )}

      {/* No Results Message */}
      {searchResults && searchResults.prospects.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center', mx: 2 }}>
          <Typography color="textSecondary">
            No prospects found matching your criteria
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ProspectsSearch;
